// src/features/purchasing2/components/ReviewGroup.jsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Purchasing2Api, getStatusColor, getStatusLabel } from '../api';

export function ReviewGroup({ 
  purchaseOrders, 
  selectedPOs, 
  onPOsSelected, 
  bootstrapData 
}) {
  const [preparingPOs, setPreparingPOs] = useState(new Set());
  const queryClient = useQueryClient();

  // Prepare PO mutation
  const preparePOMutation = useMutation(
    (poId) => Purchasing2Api.prepare(poId),
    {
      onMutate: (poId) => {
        setPreparingPOs(prev => new Set(prev).add(poId));
      },
      onSuccess: () => {
        queryClient.invalidateQueries('purchasing2-bootstrap');
      },
      onSettled: (_, __, poId) => {
        setPreparingPOs(prev => {
          const newSet = new Set(prev);
          newSet.delete(poId);
          return newSet;
        });
      },
    }
  );

  const handlePreparePO = async (poId) => {
    try {
      await preparePOMutation.mutateAsync(poId);
    } catch (error) {
      console.error('Error preparing PO:', error);
      alert('Failed to prepare purchase order');
    }
  };

  const handleSelectPO = (po) => {
    const isSelected = selectedPOs.some(selected => selected.id === po.id);
    if (isSelected) {
      onPOsSelected(selectedPOs.filter(selected => selected.id !== po.id));
    } else {
      onPOsSelected([...selectedPOs, po]);
    }
  };

  const handleSelectAll = () => {
    const draftPOs = purchaseOrders.filter(po => po.status === 'DRAFT');
    if (selectedPOs.length === draftPOs.length) {
      onPOsSelected([]);
    } else {
      onPOsSelected(draftPOs);
    }
  };

  const draftPOs = purchaseOrders.filter(po => po.status === 'DRAFT');
  const readyPOs = purchaseOrders.filter(po => po.status === 'READY_TO_SEND');

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Review & Prepare</h2>
      
      {/* Draft Purchase Orders */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-gray-700">
            Draft Orders ({draftPOs.length})
          </h3>
          {draftPOs.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {selectedPOs.length === draftPOs.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
        
        {draftPOs.length > 0 ? (
          <div className="space-y-2">
            {draftPOs.map((po) => (
              <div
                key={po.id}
                className={`p-3 border rounded-md cursor-pointer transition-colors ${
                  selectedPOs.some(selected => selected.id === po.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleSelectPO(po)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedPOs.some(selected => selected.id === po.id)}
                        onChange={() => handleSelectPO(po)}
                        className="rounded border-gray-300"
                      />
                      <span className="font-medium">{po.po}</span>
                      <span className="text-sm text-gray-500">- {po.vendorName || po.vendor}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      {po.items.length} items • Created {new Date(po.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(po.status)}`}>
                      {getStatusLabel(po.status)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreparePO(po.id);
                      }}
                      disabled={preparingPOs.has(po.id)}
                      className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 disabled:bg-gray-400"
                    >
                      {preparingPOs.has(po.id) ? 'Preparing...' : 'Prepare'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">No draft orders</div>
        )}
      </div>

      {/* Ready to Send Orders */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Ready to Send ({readyPOs.length})
        </h3>
        
        {readyPOs.length > 0 ? (
          <div className="space-y-2">
            {readyPOs.map((po) => (
              <div
                key={po.id}
                className={`p-3 border rounded-md cursor-pointer transition-colors ${
                  selectedPOs.some(selected => selected.id === po.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleSelectPO(po)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedPOs.some(selected => selected.id === po.id)}
                        onChange={() => handleSelectPO(po)}
                        className="rounded border-gray-300"
                      />
                      <span className="font-medium">{po.po}</span>
                      <span className="text-sm text-gray-500">- {po.vendorName || po.vendor}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      {po.items.length} items • Ready to send
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(po.status)}`}>
                      {getStatusLabel(po.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">No orders ready to send</div>
        )}
      </div>

      {/* Status Messages */}
      {preparePOMutation.isError && (
        <div className="mt-4 text-sm text-red-600">
          Failed to prepare purchase order
        </div>
      )}
      {preparePOMutation.isSuccess && (
        <div className="mt-4 text-sm text-green-600">
          Purchase order prepared successfully!
        </div>
      )}
    </div>
  );
}
